import { FormikValues } from "formik";
import { capitalizeString } from "../../app/utils";
import {
  BasicStep,
  PitbullProvinces,
  PreconfirmationStep,
  Option,
  MidgardQuestions,
  OnboardingType,
  CoownerData,
  StepOnePF,
  StepSixPF,
} from "../../types";
import {
  asCuit,
  getFormattedPreconfirmationFields,
  getUnificationParams,
  isAllariaMasOnboarding,
  isUnifiedOnboarding,
} from "../../utils";
import { smallStringRequired } from "../../validations";
import coownersStep from "./coownersStep";

// STEPS
import employmentProfileStep from "./employmentProfileStep";
import investmentProfileStep from "./investmentProfileStep";
import personalInformationStep from "./personalInformationStep";

const preconfirmationSteps = (
  countriesOptions: Option[],
  citizenshipOptions: Option[],
  activitiesOptions: Option[],
  getProvincesFilteredByCountry: (country?: string) => PitbullProvinces,
  provinces: PitbullProvinces,
  questions: MidgardQuestions,
  onboardingType?: OnboardingType,
) => [
  ...(isUnifiedOnboarding() && isAllariaMasOnboarding()
    ? [unificationPreconfirmationStep]
    : []),
  personalInfoPreconfirmationStep(
    countriesOptions,
    citizenshipOptions,
    getProvincesFilteredByCountry,
    provinces,
  ),
  employmentProfilePreconfirmationStep(
    activitiesOptions,
    countriesOptions,
    getProvincesFilteredByCountry,
    provinces,
  ),
  investmentProfilePreconfirmationStep(questions, onboardingType),
];

const unificationPreconfirmationStep: PreconfirmationStep = {
  name: "Unificación de cuentas",
  columns: 4,
  fields: [
    {
      name: "company",
      label: "Empresa",
      type: "text",
      valueGetter: () => "Allaria",
      isConditional: false,
    },
    {
      name: "accountId",
      label: "Número de cuenta",
      type: "cuit",
      valueGetter: () => `#${getUnificationParams().accountId}`,
      isConditional: false,
    },
    {
      name: "cuit",
      label: "CUIT / CUIL",
      type: "cuit",
      valueGetter: () => asCuit(getUnificationParams().cuit),
      isConditional: false,
    },
    {
      name: "accountName",
      label: "Nombre",
      type: "cuit",
      valueGetter: () => getUnificationParams().accountName,
      isConditional: false,
    },
  ],
};

const personalInfoPreconfirmationStep = (
  countriesOptions: Option[],
  citizenshipOptions: Option[],
  getProvincesFilteredByCountry: (country?: string) => PitbullProvinces,
  provinces: PitbullProvinces,
): PreconfirmationStep => {
  const stepFourFields = (
    personalInformationStep(
      countriesOptions,
      citizenshipOptions,
      getProvincesFilteredByCountry,
      provinces,
    ).subSteps ?? []
  ).flatMap((subStep: BasicStep) => subStep.fields ?? []);

  const stepFourFieldsFormatted =
    getFormattedPreconfirmationFields(stepFourFields);

  return {
    name: "Datos personales",
    columns: 4,
    fields: [
      {
        name: "cuit",
        label: "CUIT / CUIL",
        type: "cuit",
        formatInPreConfirmation: (value) => asCuit(value),
        isConditional: false,
      },
      ...stepFourFieldsFormatted.slice(0, 9),
      ...stepFourFieldsFormatted.slice(11, 18),
      ...stepFourFieldsFormatted.slice(9, 11),
    ],
    editStep: {
      step: 4,
      substep: 1,
    },
  };
};

const employmentProfilePreconfirmationStep = (
  activitiesOptions: Option[],
  countriesOptions: Option[],
  getProvincesFilteredByCountry: (country?: string) => PitbullProvinces,
  provinces: PitbullProvinces,
): PreconfirmationStep => {
  const stepFiveFields =
    employmentProfileStep(
      activitiesOptions,
      countriesOptions,
      getProvincesFilteredByCountry,
      provinces,
    ).fields ?? [];

  const stepFiveFieldsFormatted =
    getFormattedPreconfirmationFields(stepFiveFields);

  return {
    name: "Situación laboral",
    columns: 4,
    fields: [
      ...stepFiveFieldsFormatted.slice(0, 3),
      ...stepFiveFieldsFormatted.slice(4, 8),
    ],
    editStep: {
      step: 5,
    },
  };
};

const investmentProfilePreconfirmationStep = (
  questions: MidgardQuestions,
  onboardingType?: OnboardingType,
): PreconfirmationStep => {
  const investmentFields = (
    investmentProfileStep(questions).subSteps ?? []
  ).flatMap((subStep: BasicStep) => subStep.fields ?? []);

  const investmentFieldsFormatted =
    getFormattedPreconfirmationFields(investmentFields);

  const pickedFields = [
    ...investmentFieldsFormatted.slice(0, 6),
    investmentFieldsFormatted[8],
    investmentFieldsFormatted[6],
    investmentFieldsFormatted[7],
  ].filter(Boolean);

  const heritageFilesField = investmentFieldsFormatted.find(
    ({ name }) => name === "heritageFiles",
  );

  return {
    name: "Perfil inversor",
    columns: 2,
    fields:
      heritageFilesField && !pickedFields.includes(heritageFilesField)
        ? [...pickedFields, heritageFilesField]
        : pickedFields,
    editStep: {
      step: onboardingType === "COOWNER" ? 7 : 6,
      substep: 1,
    },
  };
};

export const coownersPreconfirmationStep = ({
  values,
  isFirstCoowner,
}: {
  values?: FormikValues | null;
  isFirstCoowner?: boolean;
}): PreconfirmationStep => {
  let updatedCoowners: {
    name: string;
    lastName: string;
    email?: string;
    idNumber?: string;
  }[] = [];

  if (isFirstCoowner) {
    updatedCoowners = (values?.coowners || []) as CoownerData[];
  } else {
    const firstCoowner = values?.steps?.find(
      (step: StepOnePF) => step["@type"] === "1_PHYSICAL",
    ) as StepOnePF;

    const originalCoowner = firstCoowner?.personalInformation;

    const coownersSteps = values?.steps?.find(
      (step: StepSixPF) => step["@type"] === "6_PHYSICAL",
    ) as StepSixPF;

    const coowners = (values?.coowners || []) as CoownerData[];

    const additionalCoownerSteps = coownersSteps?.coOwnerSteps?.find(
      (coownerSteps) =>
        coownerSteps.stepOne.personalInformation.email !== values?.email,
    );
    const additionalCoowner =
      additionalCoownerSteps?.stepOne?.personalInformation;

    const originalCoownerData = {
      email: originalCoowner.email,
      name: originalCoowner.name,
      lastName: originalCoowner.lastName,
      idNumber: originalCoowner.identityDocument.idNumber,
    };

    const currentCoownerData = {
      email: values?.email,
      name: values?.name,
      lastName: values?.lastName,
      idNumber: values?.identityDocument,
    };

    const additionalCoownersData = additionalCoowner
      ? {
          email: additionalCoowner?.email,
          name: additionalCoowner?.name,
          lastName: additionalCoowner?.lastName,
          idNumber: additionalCoowner?.identityDocument.idNumber,
        }
      : coowners.find(
          ({ email }) =>
            email !== originalCoownerData.email &&
            email !== currentCoownerData.email,
        );

    updatedCoowners = [
      originalCoownerData,
      currentCoownerData,
      ...(additionalCoownersData ? [additionalCoownersData] : []),
    ];
  }

  const currentCoownerId = values?.identityDocument || "";

  const getFieldsByCoowner = () => {
    const fields = coownersStep({}).secondaryForm?.fields || [];
    const excludedFields = ["name", "lastName", "confirmEmail"];
    const coownerFields = fields.filter(
      (field) => !excludedFields.includes(field.name),
    );

    return updatedCoowners.filter(Boolean).map((coowner, i) => {
      return getFormattedPreconfirmationFields(
        [
          {
            name: "fullName",
            type: "text",
            label: `Cotitular ${i + 1}`,
            validation: smallStringRequired,
            initialValue: "",
            columns: 2,
            valueGetter: (values) => {
              return `${values.name} ${capitalizeString(values.lastName)} ${
                currentCoownerId === values.idNumber ? "(Tú)" : ""
              }`;
            },
          },
          ...coownerFields,
        ],
        coowner,
      );
    });
  };

  return {
    name: `Resumen de cotitulares`,
    columns: 3,
    fieldsWithSeparator: getFieldsByCoowner(),
    editStep: isFirstCoowner ? { step: 6 } : undefined,
  };
};

export default preconfirmationSteps;
